/**
 * @fileoverview Organism: collapsible portfolio statistics.
 * @module components/organisms/StatsPanel
 */

import { Icon, Skeleton } from "@/components/atoms"
import {
	computeAdvancedStats,
	computePortfolioStats,
	formatCents,
	formatRatio,
} from "@/lib/domain/amortisation"
import type { Purchase } from "@/types/types"

import "./StatsPanel.css"

/** Props accepted by {@link StatsPanel}. */
export interface StatsPanelProps {
	/** Every tracked purchase, statistics are always portfolio-wide. */
	readonly purchases: readonly Purchase[]
	/** True until the first read settles. */
	readonly isLoading: boolean
	/** True when the advanced figures are shown. */
	readonly expanded: boolean
	/** Called when the panel header is activated. */
	readonly onToggle: () => void
}

/**
 * Basic totals, collapsible into a deeper breakdown.
 *
 * @description
 * Expanding trades the categories and items rows for vertical space — see
 * the parent dashboard — so the advanced figures scroll internally rather
 * than pushing the page taller.
 *
 * @param props - The collection, loading state and expand/collapse control.
 * @returns The rendered panel, or a skeleton while loading. Renders nothing
 *   once loaded if there is nothing to summarise yet.
 */
export function StatsPanel({
	purchases,
	isLoading,
	expanded,
	onToggle,
}: StatsPanelProps) {
	if (isLoading) {
		return (
			<section className="stats" aria-label="Statistiques">
				<Skeleton width="100%" height="52px" radius="12px" />
			</section>
		)
	}

	if (purchases.length === 0) return null

	const stats = computePortfolioStats(purchases)
	const advanced = expanded ? computeAdvancedStats(purchases) : null

	return (
		<section
			className={`stats${expanded ? " stats--expanded" : ""}`}
			aria-label="Statistiques"
		>
			<button
				type="button"
				className="stats__toggle"
				onClick={onToggle}
				aria-expanded={expanded}
			>
				<div className="stats__basic">
					<span className="stats__figure">
						<strong>{formatCents(stats.totalSpentCents)}</strong>
						dépensés
					</span>
					<span className="stats__figure">
						<strong>
							{stats.amortisedCount} / {stats.purchaseCount}
						</strong>
						amortis
					</span>
					<span className="stats__figure">
						<strong>
							{formatCents(stats.averageCostPerUseCents)}
						</strong>
						par utilisation
					</span>
				</div>
				<Icon name="chevron-down" size={18} />
			</button>

			{advanced !== null && (
				<div className="stats__advanced">
					<div className="stats__row">
						<span className="stats__figure">
							<strong>{advanced.totalUses}</strong>
							utilisations
						</span>
						<span className="stats__figure">
							<strong>{advanced.unusedCount}</strong>
							inutilisés
						</span>
						<span className="stats__figure">
							<strong>
								{formatRatio(advanced.averageRatio)}
							</strong>
							amorti moyen
						</span>
					</div>

					{advanced.bestValue !== null && (
						<p className="stats__best">
							🏆 Meilleur rapport :{" "}
							<strong>{advanced.bestValue.label}</strong> à{" "}
							{formatCents(advanced.bestValue.costPerUseCents)} /
							utilisation
						</p>
					)}

					{advanced.categoryBreakdown.length > 0 && (
						<ul className="stats__categories">
							{advanced.categoryBreakdown
								.slice(0, 5)
								.map(entry => (
									<li
										key={entry.category}
										className="stats__category"
									>
										<span className="stats__category-name">
											{entry.category}
										</span>
										<span className="stats__category-bar">
											<span
												className="stats__category-fill"
												style={{
													width: `${String(
														stats.totalSpentCents >
															0
															? Math.round(
																	(entry.spentCents /
																		stats.totalSpentCents) *
																		100
																)
															: 0
													)}%`,
												}}
											/>
										</span>
										<span className="stats__category-amount">
											{formatCents(entry.spentCents)}
										</span>
									</li>
								))}
						</ul>
					)}
				</div>
			)}
		</section>
	)
}
